import React, { useState } from 'react';
import { Typography, Box, Tabs, Tab, Grid, styled } from '@mui/material';
import {
  numberToCurrencyString,
  dateToLabel,
  CalendarDay,
  pluralize,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import Heading from 'components/Heading';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { AllocationRule } from 'further-types/lp/allocations';
import { sumBy } from 'lodash';
import { ActionRefLabels } from 'further-ui/labels';
import { Allocations } from 'components/AllocationsTable/types';
import RealisationAllocationsTable from './RealisationAllocationsTable';
import { ActionRef } from 'further-types/lp/action';

const TableWrapper = styled(Box)(({ theme }) => ({
  '& tbody tr': {
    '& td': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(8),
}));

interface RealisationAllocation {
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  unitsAllocated: number;
  excludeFromAllocation?: boolean;
}

interface LinkedAction {
  counterpartyLabel: string;
  actionRef: ActionRef;
  allocationRule: AllocationRule;
  actionId: string;
  ledgerDate: CalendarDay;
  totalAmountLcy: number;
  originalAllocations?: Array<
    Omit<Allocations, 'allocationAmountTcy' | 'equalisationId'>
  >;
  newAllocations: Array<
    Omit<Allocations, 'allocationAmountTcy' | 'equalisationId'>
  >;
  originalRealisationAllocations?: Array<RealisationAllocation>;
  newRealisationAllocations?: Array<RealisationAllocation>;
  newRealisationAllocationTotals: {
    totalReceivedConsiderationLcy: number;
    totalEarnoutLcy: number;
    totalEscrowLcy: number;
    totalWithholdingTaxLcy: number;
    totalUnits: number;
  };
  actionType?: string;
}

interface ActionCardProps {
  action: {
    counterpartyLabel: string;
    actionType?: string;
    allocationRule: AllocationRule;
    actionRef?: ActionRef;
    actionId: string;
    ledgerDate: CalendarDay;
    totalAmountLcy: number;
    originalAllocations?: Array<
      Omit<Allocations, 'allocationAmountTcy' | 'equalisationId'>
    >;
    newAllocations?: Array<
      Omit<Allocations, 'allocationAmountTcy' | 'equalisationId'>
    >;
    originalRealisationAllocations?: Array<RealisationAllocation>;
    newRealisationAllocations?: Array<RealisationAllocation>;
    linkedActions?: Array<LinkedAction>;
  };
  actionIndex: number;
  baseCurrency: CurrencyCode;
  isLastElement: boolean;
  partnerTransferSection: PartnerTransferSection;
  transferDate?: CalendarDay;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  actionIndex,
  baseCurrency,
  isLastElement,
  partnerTransferSection,
  transferDate,
}) => {
  const isManualAllocationRule =
    action.allocationRule === AllocationRule.Manual;

  const [activeTab, setActiveTab] = useState(1);

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const transferDateLabel = transferDate
    ? `(${transferDate.toLocalDateLabel()})`
    : 'transfer date';

  const baseActionLabel = action.actionRef
    ? ActionRefLabels[action.actionRef]
    : action.actionType;

  const actionTypeLabel =
    action.linkedActions && action.linkedActions.length > 0
      ? baseActionLabel +
        ` and ${action.linkedActions.length} ${pluralize(
          'linked action',
          action.linkedActions.length,
        )}`
      : baseActionLabel;

  const concertinaItems = [
    <Typography key="counterparty" variant="body2">
      {isManualAllocationRule
        ? `${action.counterpartyLabel}*`
        : action.counterpartyLabel}
    </Typography>,
    <Typography key="type" variant="body2">
      {actionTypeLabel}
    </Typography>,
    <Typography key="date" variant="body2">
      {dateToLabel(action.ledgerDate.toUTCDate())}
    </Typography>,
    <Typography key="amount" variant="body2">
      {numberToCurrencyString(action.totalAmountLcy, {
        currency: baseCurrency,
      })}
    </Typography>,
  ];

  const renderAllocationsTable = (
    allocations: Array<
      Omit<Allocations, 'allocationAmountTcy' | 'equalisationId'>
    >,
  ) => {
    if (!allocations || allocations.length === 0) {
      return <Typography variant="body2">No allocations</Typography>;
    }

    const columns = [
      {
        header: 'Investor',
        width: '15%',
        renderCell: (row: any) => row.investorLabel,
        renderFooter: () =>
          `${
            allocations.filter((a) => !a.excludeFromAllocation).length
          } investors`,
      },
      {
        header: 'Total commitment',
        width: '12%',
        renderCell: (row: any) =>
          row.totalCommitmentLcy
            ? numberToCurrencyString(row.totalCommitmentLcy, {
                currency: baseCurrency,
              })
            : '-',
        renderFooter: () => '',
      },
      {
        header: 'Unallocated commitment',
        width: '12%',
        renderCell: (row: any) =>
          row.unallocatedCommitmentLcy
            ? numberToCurrencyString(row.unallocatedCommitmentLcy, {
                currency: baseCurrency,
              })
            : '-',
        renderFooter: () => '',
      },
      {
        header: 'Close date',
        width: '12%',
        renderCell: (row: any) => dateToLabel(row.closeDate),
        renderFooter: () => '',
      },
      {
        header: 'Allocation allocated',
        width: '14%',
        renderCell: (row: any) =>
          numberToCurrencyString(row.allocationAmountLcy, {
            currency: baseCurrency,
          }),
        renderFooter: () =>
          numberToCurrencyString(
            sumBy(
              allocations.filter((a) => !a.excludeFromAllocation),
              'allocationAmountLcy',
            ),
            { currency: baseCurrency },
          ),
      },
      {
        header: 'Allocation allocated (%)',
        width: '14%',
        renderCell: (row: any) => `${row.allocationPercentage.toFixed(2)}%`,
        renderFooter: () =>
          `${sumBy(
            allocations.filter((a) => !a.excludeFromAllocation),
            'allocationPercentage',
          ).toFixed(2)}%`,
      },
      {
        header: 'Exclude investor from allocation',
        width: '11%',
        renderCell: (row: any) => (row.excludeFromAllocation ? 'Yes' : 'No'),
        renderFooter: () => '',
      },
    ];

    const rows = allocations.map((allocation, idx) => ({
      key: idx,
      ...allocation,
    }));

    const allocationRuleOptions = [
      { value: AllocationRule.Manual, label: 'Manual' },
      {
        value: AllocationRule.ByCommitmentAndClosingDate,
        label: 'By Commitment and Closing Date',
      },
      { value: AllocationRule.LinkedAction, label: 'Linked Action' },
      {
        value: AllocationRule.PerOriginalInvestment,
        label: 'Per Original Investment',
      },
    ];

    return (
      <TableWrapper>
        <EqualisationsAllocationsTable
          columns={columns}
          rows={rows}
          showAllocationRule={true}
          allocationRule={action.allocationRule}
          allocationRuleOptions={allocationRuleOptions}
          isNotEditable={true}
        />
      </TableWrapper>
    );
  };

  const expandedContent = (
    <>
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        sx={{ marginBottom: 8 }}
      >
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation before {transferDateLabel}
            </Heading>
          }
        />
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation after {transferDateLabel}
            </Heading>
          }
        />
      </StyledTabs>

      <Grid size={{ xs: 12, lg: 12 }}>
        {activeTab === 0 && (
          <>
            {action.originalRealisationAllocations &&
            action.originalRealisationAllocations.length > 0 ? (
              <RealisationAllocationsTable
                realisationAllocations={action.originalRealisationAllocations}
                baseCurrency={baseCurrency}
                allocationRule={action.allocationRule}
              />
            ) : action.originalAllocations &&
              action.originalAllocations.length > 0 ? (
              renderAllocationsTable(action.originalAllocations)
            ) : (
              <Typography variant="body2">No allocations</Typography>
            )}
          </>
        )}
        {activeTab === 1 && (
          <>
            {action.newRealisationAllocations &&
            action.newRealisationAllocations.length > 0 ? (
              <RealisationAllocationsTable
                realisationAllocations={action.newRealisationAllocations}
                baseCurrency={baseCurrency}
                allocationRule={action.allocationRule}
              />
            ) : action.newAllocations && action.newAllocations.length > 0 ? (
              renderAllocationsTable(action.newAllocations)
            ) : (
              <Typography variant="body2">No allocations</Typography>
            )}
          </>
        )}
      </Grid>

      {action.linkedActions &&
        action.linkedActions.length > 0 &&
        action.linkedActions.map((linkedAction, linkedIndex) => (
          <ActionCard
            key={linkedIndex}
            action={{
              counterpartyLabel: linkedAction.counterpartyLabel,
              actionRef: linkedAction.actionRef,
              allocationRule: linkedAction.allocationRule,
              actionId: linkedAction.actionId,
              ledgerDate: linkedAction.ledgerDate,
              totalAmountLcy: linkedAction.totalAmountLcy,
              originalAllocations: linkedAction.originalAllocations,
              newAllocations: linkedAction.newAllocations,
              originalRealisationAllocations:
                linkedAction.originalRealisationAllocations,
              newRealisationAllocations: linkedAction.newRealisationAllocations,
              linkedActions: [],
              actionType:
                linkedAction.actionType ??
                (linkedAction.actionRef
                  ? ActionRefLabels[linkedAction.actionRef]
                  : ''),
            }}
            actionIndex={actionIndex}
            baseCurrency={baseCurrency}
            isLastElement={
              linkedIndex === (action.linkedActions?.length ?? 0) - 1
            }
            partnerTransferSection={partnerTransferSection}
            transferDate={transferDate}
          />
        ))}
    </>
  );

  return (
    <EqualisationsConcertina
      isExpandedByDefault={isManualAllocationRule}
      concertinaItems={concertinaItems}
      expandedContent={expandedContent}
      isLastElement={isLastElement}
      gridTemplateColumns="40% 20% 19% 19% 2%"
    />
  );
};

export default ActionCard;
